'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  getSubsidiaryConfigService,
  subsidiaryQueryFn,
  SUBSIDIARY_CONFIG_QUERY_KEY,
  SUBSIDIARY_QUERY_KEY,
} from '@/services/Supplier';
import { ResponsibleColorProvider } from '@/contexts/ResponsibleColorContext';
import { SUBSIDIARY_USERS_QUERY_KEY } from '@/modules/salesReport/SalesReport.constants';
import { CURRENT_USER_QUERY_KEY } from '@/hooks/UseCurrentUser';
import { productAnalytics } from '@/analytics';
import { trackSlowRequests } from './trackSlowRequests';
import { trackSlowFetches } from './trackSlowFetches';

const queryClient = new QueryClient();

trackSlowRequests(queryClient, (properties) =>
  productAnalytics.trackEvent('api_request_degraded', properties)
);
trackSlowFetches(process.env.NEXT_PUBLIC_API_URL, (properties) =>
  productAnalytics.trackEvent('api_fetch_slow', properties)
);

// OJO: setQueryDefaults hace match por PREFIJO. Estos defaults aplican a toda key
// que empiece por ['subsidiary']. Por eso el listado de usuarios usa la key
// ['subsidiary-users'] (ver SUBSIDIARY_USERS_QUERY_KEY) y NO ['subsidiary', 'users']:
// de lo contrario heredaría este staleTime de 1h, no refetcharía y quedaría vacío.
queryClient.setQueryDefaults(SUBSIDIARY_QUERY_KEY, {
  queryFn: subsidiaryQueryFn,
  staleTime: 1000 * 60 * 60, // 1 hour stale time for subsidiary config
});

// Edit views: refetch on mount (server truth), never on focus (would clobber
// in-progress edits). useSubsidiaryConfig overrides this for cached display.
queryClient.setQueryDefaults(SUBSIDIARY_CONFIG_QUERY_KEY, {
  queryFn: ({ signal }) => getSubsidiaryConfigService({ signal }),
  refetchOnMount: 'always',
  refetchOnWindowFocus: false,
});

// Chat panels mount in waves; 10s keeps later mounts from refetching these.
for (const key of [
  SUBSIDIARY_USERS_QUERY_KEY,
  ['client', 'search'],
  CURRENT_USER_QUERY_KEY,
]) {
  queryClient.setQueryDefaults(key, { staleTime: 10_000 });
}

// Templates proxy to wali (~260 ms) and change only from settings, which
// invalidates this key; 5 min also bounds Meta-side approval/status changes.
queryClient.setQueryDefaults(['templateMessages'], {
  staleTime: 5 * 60 * 1000,
});

const TanstackQueryConfig = (props: { children: React.ReactNode }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <ResponsibleColorProvider>{props.children}</ResponsibleColorProvider>
    </QueryClientProvider>
  );
};

export default TanstackQueryConfig;
